import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowLeft, Save, Upload, ChevronRight, ChevronLeft, Plus, Trash2, GripVertical, ArrowUpLeft } from 'lucide-react'
import { contractTemplatesApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { RichTextEditor, RichTextEditorRef } from '@/components/ui/rich-text-editor'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import type { CreateContractTemplateRequest, UpdateContractTemplateRequest, TemplateField, ContractFieldType } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

interface FormData {
  template_name: string
  description: string
  content: string
  is_default: boolean
  is_active: boolean
}

const FIELD_TYPES: { value: ContractFieldType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'TEXTAREA', label: 'Text Area' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'RADIO', label: 'Radio' },
]

interface ContractTemplateFormPageProps {
  mode?: 'create' | 'edit' | 'view'
}

export default function ContractTemplateFormPage({ mode: propMode }: ContractTemplateFormPageProps = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const isEdit = propMode === 'edit' || (!!id && id !== 'new' && propMode !== 'view')
  const isViewMode = propMode === 'view'
  // Extended TemplateField with stable _id for React keys
  type FieldWithId = TemplateField & { _id: string }
  const [fields, setFields] = useState<FieldWithId[]>([])
  const [showFieldsPanel, setShowFieldsPanel] = useState(true)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [editorContent, setEditorContent] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editorRef = useRef<RichTextEditorRef>(null)

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      template_name: '',
      description: '',
      content: '',
      is_default: false,
      is_active: true,
    },
  })

  const { data: template, isLoading } = useQuery({
    queryKey: ['contractTemplate', id],
    queryFn: () => contractTemplatesApi.getById(id!),
    enabled: isEdit,
  })

  useEffect(() => {
    if (template) {
      reset({
        template_name: template.template_name,
        description: template.description || '',
        content: template.content,
        is_default: template.is_default,
        is_active: template.is_active,
      })
      setEditorContent(template.content || '')
      // Load fields from template with stable IDs
      if (template.fields) {
        setFields(template.fields.map((f, i) => ({ ...f, _id: `existing_${i}_${Date.now()}` })))
      }
    }
  }, [template, reset])

  // Handle Word document upload
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ]
    if (!validTypes.includes(file.type) && !file.name.endsWith('.docx') && !file.name.endsWith('.doc')) {
      toast.error('Please upload a Word document (.doc or .docx)')
      return
    }

    setUploadingDoc(true)
    try {
      const mammoth = await import('mammoth')
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.convertToHtml({ arrayBuffer })

      if (result.value) {
        setEditorContent(result.value)
        setValue('content', result.value)
        toast.success('Document imported successfully')
      }
    } catch {
      toast.error('Failed to parse document. Please try a different file.')
    } finally {
      setUploadingDoc(false)
      e.target.value = ''
    }
  }

  // Sync editor content with form
  const handleEditorChange = (html: string) => {
    setEditorContent(html)
    setValue('content', html)
  }

  // Initialize editor content from form
  useEffect(() => {
    const content = watch('content')
    if (content && content !== editorContent) {
      setEditorContent(content)
    }
  }, [watch('content')])

  // Field management functions
  const addField = () => {
    const fieldId = `field_${Date.now()}`
    const newField: TemplateField & { _id: string } = {
      _id: fieldId,
      field_name: '',
      field_label: '',
      field_type: 'TEXT',
      is_required: false,
      display_order: fields.length,
    }
    setFields([...fields, newField])
  }

  const updateField = (index: number, updates: Partial<TemplateField>) => {
    const newFields = [...fields]
    newFields[index] = { ...newFields[index], ...updates }
    setFields(newFields)
  }

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index))
  }

  const insertFieldPlaceholder = (fieldName: string) => {
    const placeholder = `{{${fieldName}}}`
    editorRef.current?.insertText(placeholder)
  }

  const createMutation = useMutation({
    mutationFn: (data: CreateContractTemplateRequest) => contractTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] })
      toast.success('Template created successfully')
      navigate('/contracts/templates')
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || error.message
      toast.error(errorMsg || 'Failed to create template')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateContractTemplateRequest) => contractTemplatesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['contractTemplate', id] })
      toast.success('Template updated successfully')
      navigate('/contracts/templates')
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || error.message
      toast.error(errorMsg || 'Failed to update template')
    },
  })

  const onSubmit = (data: FormData) => {
    // Strip _id from fields and filter out incomplete fields before sending to API
    const validFields = fields.filter(f => f.field_name && f.field_label && f.field_type)
    const cleanFields = validFields.length > 0
      ? validFields.map(({ _id, ...rest }) => rest)
      : undefined

    if (isEdit) {
      updateMutation.mutate({
        template_name: data.template_name,
        description: data.description || undefined,
        content: data.content,
        is_default: data.is_default,
        is_active: data.is_active,
        fields: cleanFields,
      })
    } else {
      createMutation.mutate({
        template_name: data.template_name,
        description: data.description || undefined,
        content: data.content,
        is_default: data.is_default,
        fields: cleanFields,
      })
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  if ((isEdit || isViewMode) && isLoading) {
    return <div className="text-center py-8">Loading...</div>
  }

  const pageTitle = isViewMode ? 'View Template' : isEdit ? 'Edit Template' : 'Create Template'
  const pageDescription = isViewMode
    ? 'View contract template details'
    : isEdit
      ? 'Update your contract template'
      : 'Create a new contract template'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts/templates')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{pageTitle}</h1>
            <p className="text-muted-foreground">{pageDescription}</p>
          </div>
        </div>
        {isViewMode && canAddEdit('CONTRACT_TEMPLATES') && (
          <Button onClick={() => navigate(`/contracts/templates/${id}/edit`)}>
            Edit Template
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Template Details Card */}
        <Card>
          <CardHeader>
            <CardTitle>Template Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="template_name">Template Name *</Label>
                <Input
                  id="template_name"
                  {...register('template_name', { required: 'Template name is required' })}
                  placeholder="e.g., Standard Service Agreement"
                  disabled={isViewMode}
                />
                {errors.template_name && (
                  <p className="text-sm text-destructive">{errors.template_name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  {...register('description')}
                  placeholder="Brief description of this template"
                  disabled={isViewMode}
                />
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_default"
                  checked={watch('is_default')}
                  onCheckedChange={(checked) => setValue('is_default', checked)}
                  disabled={isViewMode}
                />
                <Label htmlFor="is_default">Set as default template</Label>
              </div>
              {(isEdit || isViewMode) && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={watch('is_active')}
                    onCheckedChange={(checked) => setValue('is_active', checked)}
                    disabled={isViewMode}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Split Layout: Document Editor + Fields Panel */}
        <div className="flex gap-4">
          {/* Main Document Editor */}
          <div className="flex-1">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Contract Content</CardTitle>
                    <CardDescription>
                      {isViewMode ? 'View the template content' : 'Edit the template content or upload a Word file'}
                    </CardDescription>
                  </div>
                  {!isViewMode && (
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleDocumentUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingDoc}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingDoc ? 'Importing...' : 'Upload Word Doc'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  ref={editorRef}
                  content={editorContent}
                  onChange={handleEditorChange}
                  placeholder="Start typing your contract template or upload a Word document. Use placeholders like {{client_name}}, {{date}}, etc."
                  minHeight="450px"
                  maxHeight="500px"
                  editable={!isViewMode}
                />
                {errors.content && <p className="text-sm text-destructive mt-2">{errors.content.message}</p>}
                {!isViewMode && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Available placeholders: {'{{client_name}}'}, {'{{client_email}}'}, {'{{date}}'}, {'{{contract_number}}'}, {'{{agency_name}}'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Toggle Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto py-4 px-1 self-start mt-16"
            onClick={() => setShowFieldsPanel(!showFieldsPanel)}
          >
            {showFieldsPanel ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>

          {/* Fields Panel */}
          <div className={`transition-all duration-300 overflow-hidden ${showFieldsPanel ? 'w-96' : 'w-0'}`}>
            {showFieldsPanel && (
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Form Fields</CardTitle>
                      <CardDescription className="text-xs">
                        {isViewMode ? 'Fields defined for this template' : 'Define fields users must fill when using this template'}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary">{fields.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px] px-4 pb-4">
                    <div className="space-y-4">
                      {fields.map((field, index) => (
                        <div key={field._id} className="p-3 border rounded-lg space-y-3 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {!isViewMode && <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />}
                              <span className="text-sm font-medium">Field {index + 1}</span>
                            </div>
                            {!isViewMode && (
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => removeField(index)}
                                >
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs">Field Label</Label>
                            <Input
                              value={field.field_label}
                              onChange={(e) => updateField(index, {
                                field_label: e.target.value,
                                field_name: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                              })}
                              placeholder="e.g., Client Name"
                              className="h-8 text-sm"
                              disabled={isViewMode}
                            />
                          </div>

                          {/* Insert placeholder button */}
                          {field.field_name && !isViewMode && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full h-7 text-xs"
                              onClick={() => insertFieldPlaceholder(field.field_name)}
                            >
                              <ArrowUpLeft className="h-3 w-3 mr-1" />
                              Insert {`{{${field.field_name}}}`}
                            </Button>
                          )}

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Type</Label>
                              <Select
                                value={field.field_type}
                                onValueChange={(v) => updateField(index, { field_type: v as ContractFieldType })}
                                disabled={isViewMode}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {FIELD_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Placeholder</Label>
                              <Input
                                value={field.placeholder || ''}
                                onChange={(e) => updateField(index, { placeholder: e.target.value })}
                                placeholder="Hint text"
                                className="h-8 text-sm"
                                disabled={isViewMode}
                              />
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`required-${index}`}
                              checked={field.is_required}
                              onCheckedChange={(checked) => updateField(index, { is_required: !!checked })}
                              disabled={isViewMode}
                            />
                            <Label htmlFor={`required-${index}`} className="text-xs font-normal">Required field</Label>
                          </div>

                          {(field.field_type === 'DROPDOWN' || field.field_type === 'RADIO') && (
                            <div className="space-y-1">
                              <Label className="text-xs">Options (comma-separated)</Label>
                              <Input
                                value={field.options?.map(o => o.label).join(', ') || ''}
                                onChange={(e) => {
                                  const opts = e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                  updateField(index, {
                                    options: opts.map(label => ({ label, value: label.toLowerCase().replace(/\s+/g, '_') }))
                                  })
                                }}
                                placeholder="Option 1, Option 2, Option 3"
                                className="h-8 text-sm"
                                disabled={isViewMode}
                              />
                            </div>
                          )}
                        </div>
                      ))}

                      {!isViewMode && (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={addField}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Field
                        </Button>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {!isViewMode && (
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/contracts/templates')}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              <Save className="mr-2 h-4 w-4" />
              {isSubmitting ? 'Saving...' : isEdit ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}

